import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import _ from 'lodash';
import scrollIntoView from 'dom-scroll-into-view';
import Dropdown from 'components/clarity-ui/inputs/dropdown/dropdown';
import DropdownMenu from 'components/clarity-ui/inputs/dropdown/dropdownMenu';

/**
 * Autocomplete component.
 *
 * Usage:
 *
 * ```Javascript
 * <Autocomplete
 *   inputProps={additionalPropsArray}
 *   items={objectArray}
 *   getItemValue={(item) => item.name}
 *   shouldItemRender={function}
 *   sortItems={function}
 *   renderItem={(label, isHighlighted, styles) => (
 *      <li
 *        className='DropDown-menuItem'
 *        style={isHighlighted ? styles.highlightedItem : styles.label}
 *        key={label.id}
 *      >{label.name}</li>
 *   )}
 * />
 * ```
 *
 * @extends {React.Component}
 * @property {String} initialValue Sets the initial value for the component.
 * @property {Array} inputProps Sets aditional props to the input text element of the component.
 * @property {Array} items Sets the array of objects the component will manage.
 * @property {Function} onChange Callback on change input.
 * @property {Function} onSelect Callback on select value from dropdown.
 * @property {Function} getItemValue Callback to define the property to render.
 * @property {Function} shouldItemRender Callback to define wheter to render or not an item.
 * @property {Function} sortItems Callback to define how to order the list of elements to be rendered.
 * @property {Function} renderItem Callback to define how the elements will be rendered.
 * @property {Function} renderMenu Callback to define how the menu will be rendered.
 */
class Autocomplete extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      value: this.props.initialValue || '',
      filteredItems: this.props.items || [],
      isOpen: false,
      highlightedIndex: null
    };
    this.ignoreBlur = false;
    this.performAutoCompleteOnUpdate = false;
    this.performAutoCompleteOnKeyUp = false;
    this.keyDownHandlers = {
      ArrowDown (event) {
        event.preventDefault();
        var highlightedIndex = this.state.highlightedIndex;

        var index = highlightedIndex === null || highlightedIndex === this.getFilteredItems().length - 1 ? 0 : highlightedIndex + 1;
        this.performAutoCompleteOnKeyUp = true;
        this.setState({
          highlightedIndex: index,
          isOpen: true
        });
      },

      ArrowUp (event) {
        event.preventDefault();
        var highlightedIndex = this.state.highlightedIndex;

        var index = highlightedIndex === 0 || highlightedIndex === null ? this.getFilteredItems().length - 1 : highlightedIndex - 1;
        this.performAutoCompleteOnKeyUp = true;
        this.setState({
          highlightedIndex: index,
          isOpen: true
        });
      },

      Enter () {
        var _this2 = this;

        if (this.state.isOpen === false) {
          // already selected this, do nothing
          return;
        } else if (this.state.highlightedIndex == null) {
          // hit enter after focus but before typing anything so no autocomplete attempt yet
          this.setState({
            isOpen: false
          }, function () {
            ReactDOM.findDOMNode(_this2.refs.input).select();
          });
        } else {
          var item = this.getFilteredItems()[this.state.highlightedIndex];
          this.setState({
            value: this.props.getItemValue(item),
            isOpen: false,
            highlightedIndex: null
          }, function () {
            //React.findDOMNode(this.refs.input).focus() // TODO: file issue
            ReactDOM.findDOMNode(_this2.refs.input).setSelectionRange(_this2.state.value.length, _this2.state.value.length);
            _this2.props.onSelect(_this2.state.value, item);
          });
        }
      },

      Escape () {
        this.setState({
          highlightedIndex: null,
          isOpen: false
        });
      }
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const newState = {};
    const items = this.getFilteredItems();

    if (!_.isUndefined(this.props.items) && !_.isUndefined(prevProps.items) && !_.isEqual(this.props.items, prevProps.items)) {
      var valueInNextProps = _.some(this.props.items, (item) => {return _.contains(this.props.getItemValue(item).toLowerCase(), this.refs.input.value.toLowerCase())});
      if (!valueInNextProps && this.props.shouldClear) {
        _.assign(newState, {value: ''});
      }
    }
    if (!_.isEqual(this.state.filteredItems, items)) _.assign(newState, {filteredItems: items});

    if (this.state.isOpen === true && prevState.isOpen === false) _.assign(newState, this.setMenuPositions());

    if (!_.isEmpty(newState)) this.setState(newState);

    this.maybeScrollItemIntoView();
  }

  componentWillReceiveProps (nextProps) {
    if (!_.isUndefined(nextProps.value)) {
      this.setState({
        value: nextProps.value
      });
    }
  }

  _defaultMenu (items, value, style) {
    return (
      <DropdownMenu isActive style={style}>
        {items}
      </DropdownMenu>
    );
  }

  suggestionsMenu () {
    if (_.isUndefined(this.props.renderMenu)) {
      return this._defaultMenu.apply(this, arguments);
    } else {
      return this.props.renderMenu.apply(this, arguments);
    }
  }

  maybeScrollItemIntoView () {
    if (this.state.isOpen === true && this.state.highlightedIndex !== null) {
      var itemNode = ReactDOM.findDOMNode(this.refs['item-' + this.state.highlightedIndex]);
      var menuNode = ReactDOM.findDOMNode(this.refs.menu);
      scrollIntoView(itemNode, menuNode, { onlyScrollIfNeeded: true });
    }
  }

  handleKeyDown (event) {
    if (this.keyDownHandlers[event.key]) {
      this.keyDownHandlers[event.key].call(this, event);
    } else {
      this.setState({
        highlightedIndex: null,
        isOpen: true
      });
    }
  }

  handleChange (event) {
    var _this = this;

    this.performAutoCompleteOnKeyUp = true;
    this.setState({
      value: event.target.value
    }, function () {
      _this.props.onChange(event, _this.state.value);
    });
  }

  handleKeyUp (event) {
    if (this.performAutoCompleteOnKeyUp) {
      this.performAutoCompleteOnKeyUp = false;
      this.maybeAutoCompleteText();
    }
    if (!!this.props.onHandleKeyUp) this.props.onHandleKeyUp(event);
  }

  getFilteredItems () {
    var _this3 = this;
    var items = this.props.items;
    if (items) {
      items = items.filter(function (item) {
        return _this3.props.shouldItemRender(item, _this3.state.value);
      });

      if (this.props.sortItems) {
        items.sort(function (a, b) {
          return _this3.props.sortItems(a, b, _this3.state.value);
        });
      }
    } else {
      items = [];
    }

    return items;
  }

  maybeAutoCompleteText () {
    var _this4 = this;

    if (this.state.value === '') return;
    var highlightedIndex = this.state.highlightedIndex;

    var items = this.getFilteredItems();
    if (items.length === 0) {
      // this.setState({
      //   highlightedIndex: null,
      //   isOpen: false
      // });
      return;
    }
    var matchedItem = highlightedIndex !== null ? items[highlightedIndex] : items[0];
    var itemValue = this.props.getItemValue(matchedItem);
    var itemValueDoesMatch = itemValue.toLowerCase().indexOf(this.state.value.toLowerCase()) === 0;

    var node = ReactDOM.findDOMNode(this.refs.input);
    var setSelection = function setSelection() {
      node.value = itemValue;
      if (itemValueDoesMatch) node.setSelectionRange(_this4.state.value.length, itemValue.length);
    };
    if (highlightedIndex === null) this.setState({ highlightedIndex: 0 }, setSelection);else setSelection();
  }

  setMenuPositions () {
    var node = ReactDOM.findDOMNode(this.refs.input);
    var rect = node.getBoundingClientRect();
    var computedStyle = getComputedStyle(node);
    var marginBottom = parseInt(computedStyle.marginBottom, 10);
    var marginLeft = parseInt(computedStyle.marginLeft, 10);
    var marginRight = parseInt(computedStyle.marginRight, 10);
    return {
      menuTop: rect.bottom + marginBottom,
      menuLeft: rect.left + marginLeft,
      menuWidth: rect.width + marginLeft + marginRight
    };
  }

  highlightItemFromMouse (index) {
    this.setState({ highlightedIndex: index });
  }

  selectItemFromMouse (item) {
    var _this5 = this;

    this.setState({
      value: this.props.getItemValue(item),
      isOpen: false,
      highlightedIndex: null
    }, function () {
      _this5.props.onSelect(_this5.state.value, item);
      ReactDOM.findDOMNode(_this5.refs.input).focus();
      _this5.setIgnoreBlur(false);
    });
  }

  setIgnoreBlur (ignore) {
    this.ignoreBlur = ignore;
  }

  renderMenu () {
    var _this6 = this;

    var items = this.getFilteredItems().map(function (item, index) {
      var element = _this6.props.renderItem(item, _this6.state.highlightedIndex === index, { cursor: 'default' });
      return React.cloneElement(element, {
        onMouseDown: function onMouseDown() {
          return _this6.setIgnoreBlur(true);
        },
        onMouseEnter: function onMouseEnter() {
          return _this6.highlightItemFromMouse(index);
        },
        onClick: function onClick() {
          return _this6.selectItemFromMouse(item);
        },
        ref: 'item-' + index
      });
    });
    var style = {
      left: this.state.menuLeft,
      top: this.state.menuTop,
      minWidth: this.state.menuWidth
    };
    var menu = this.suggestionsMenu(items, this.state.value, style);
    return React.cloneElement(menu, { ref: 'menu' });
  }

  getActiveItemValue () {
    if (this.state.highlightedIndex === null) return '';else {
      var item = this.state.filteredItems[this.state.highlightedIndex];
      return this.props.getItemValue(item);
    }
  }

  handleInputBlur () {
    if (this.ignoreBlur) return;
    this.setState({
      isOpen: false,
      highlightedIndex: null
    });
  }

  handleInputFocus () {
    if (this.ignoreBlur) return;
    this.setState({ isOpen: true });
  }

  handleInputClick () {
    if (this.state.isOpen === false) this.setState({ isOpen: true });
  }

  /**
   * @return {String} Button CSS classname.
   */
  getButtonClassName () {
    return classNames({
      'InputField-iconButton': true,
      'Icon': true,
      'Icon--chevronDown': !this.state.isOpen,
      'Icon--chevronUp': this.state.isOpen
    });
  }
  /**
  * Render the component
  *
  * @return {JSX} JSX component markup.
  **/
  render () {
    var _this7 = this;

    return (
      <Dropdown>
        <div className="InputField-withIconButton">
          <div className="InputField-icon Icon Icon--search"></div>
          <input
            {...this.props.inputProps}
            className="InputField-input InputField-input--withIcon InputField-input--withIconButton"
            role="combobox"
            aria-autocomplete="both"
            aria-label={this.getActiveItemValue()}
            ref="input"
            onFocus={this.handleInputFocus.bind(this)}
            onBlur={this.handleInputBlur.bind(this)}
            onChange={(event) => (
              _this7.handleChange(event)
            )}
            onKeyDown={(event) => (
              _this7.handleKeyDown(event)
            )}
            onKeyUp={(event) => (
              _this7.handleKeyUp(event)
            )}
            onClick={this.handleInputClick.bind(this)}
            value={this.state.value}
          />
        <button
          className={this.getButtonClassName()}
          onClick={this.handleInputClick.bind(this)}
          onBlur={this.handleInputBlur.bind(this)}
        >
        </button>
        </div>
        {this.state.isOpen && this.renderMenu()}
      </Dropdown>
    )
  }
}

Autocomplete.propTypes = {
  initialValue: React.PropTypes.any,
  onChange: React.PropTypes.func,
  onSelect: React.PropTypes.func,
  shouldItemRender: React.PropTypes.func.isRequired,
  renderItem: React.PropTypes.func.isRequired,
  renderMenu: React.PropTypes.func,
  menuStyle: React.PropTypes.object,
  inputProps: React.PropTypes.object,
  shouldClear: React.PropTypes.bool
};

Autocomplete.defaultProps = {
  inputProps: {},
  onChange: () => {},
  onSelect: () => {},
  shouldClear: true
}

export default Autocomplete;



// WEBPACK FOOTER //
// ./src/components/clarity-ui/inputs/autocomplete.jsx